import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { HeatmapVisualization } from './HeatmapVisualization';
import { SessionReplayPlayer } from './SessionReplayPlayer';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  TrendingUp,
  ArrowRight,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';

interface VisitorSession {
  id: string;
  visitor_id: string;
  session_id: string;
  started_at: string;
  last_activity_at: string;
  ended_at: string | null;
  is_returning: boolean;
  device: string;
  browser: string;
  country: string;
  city: string;
  pages_viewed: number;
  total_duration_ms: number;
  bounce: boolean;
}

interface JourneyStep {
  page_path: string;
  page_title: string;
  viewed_at: string;
  duration_ms: number;
  events_count: number;
}

interface Stats {
  totalSessions: number;
  uniqueVisitors: number;
  returningVisitors: number;
  newVisitors: number;
  avgDurationMs: number;
  bounceRate: number;
}

export function VisitorTrackingDashboard() {
  const [sessions, setSessions] = useState<VisitorSession[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalSessions: 0,
    uniqueVisitors: 0,
    returningVisitors: 0,
    newVisitors: 0,
    avgDurationMs: 0,
    bounceRate: 0,
  });
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [journey, setJourney] = useState<JourneyStep[]>([]);
  const [replayEvents, setReplayEvents] = useState<any[]>([]);
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);

      // Calculate date range
      const now = new Date();
      const ranges: Record<string, number> = {
        '24h': 1,
        '7d': 7,
        '30d': 30,
        '90d': 90,
      };
      const daysAgo = ranges[timeRange] || 7;
      const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

      // Fetch sessions
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('visitor_sessions')
        .select('*')
        .gte('started_at', startDate.toISOString())
        .order('started_at', { ascending: false })
        .limit(500);

      if (sessionsError) throw sessionsError;

      setSessions(sessionsData || []);

      // Calculate stats
      const uniqueVisitorIds = new Set(sessionsData?.map((s) => s.visitor_id) || []);
      const returningCount = sessionsData?.filter((s) => s.is_returning).length || 0;
      const bounceCount = sessionsData?.filter((s) => s.bounce).length || 0;
      const totalDuration =
        sessionsData?.reduce((sum, s) => sum + (s.total_duration_ms || 0), 0) || 0;

      setStats({
        totalSessions: sessionsData?.length || 0,
        uniqueVisitors: uniqueVisitorIds.size,
        returningVisitors: returningCount,
        newVisitors: uniqueVisitorIds.size - returningCount,
        avgDurationMs: sessionsData?.length ? totalDuration / sessionsData.length : 0,
        bounceRate: sessionsData?.length ? (bounceCount / sessionsData.length) * 100 : 0,
      });
    } catch (error: any) {
      toast.error('Failed to load visitor data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadJourney = async (sessionId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_visitor_journey', {
        p_session_id: sessionId,
      });

      if (error) throw error;
      setJourney(data || []);
    } catch (error) {
      console.error('Error loading journey:', error);
      toast.error('Failed to load visitor journey');
    }
  };

  const loadReplay = async (sessionId: string) => {
    try {
      const { data, error } = await supabase
        .from('visitor_session_replays')
        .select('events')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;
      setReplayEvents(Array.isArray(data?.events) ? data.events : []);
    } catch (error) {
      console.error('Error loading replay:', error);
      setReplayEvents([]);
    }
  };

  useEffect(() => {
    loadData();
  }, [timeRange]);

  useEffect(() => {
    if (selectedSession) {
      loadJourney(selectedSession);
      loadReplay(selectedSession);
    }
  }, [selectedSession]);

  const formatDuration = (ms: number) => {
    if (!ms || ms < 1000) return '< 1s';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Visitor Tracking</h1>
          <p className="text-muted-foreground">
            Detailed analytics on unique and returning visitors
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={loadData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalSessions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Unique Visitors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uniqueVisitors}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Returning
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.returningVisitors}</div>
            <p className="text-xs text-muted-foreground">
              {stats.uniqueVisitors
                ? Math.round((stats.returningVisitors / stats.uniqueVisitors) * 100)
                : 0}
              %
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <UserX className="h-4 w-4" />
              New Visitors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.newVisitors}</div>
            <p className="text-xs text-muted-foreground">
              {stats.uniqueVisitors
                ? Math.round((stats.newVisitors / stats.uniqueVisitors) * 100)
                : 0}
              %
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Avg. Duration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDuration(stats.avgDurationMs)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Bounce Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Math.round(stats.bounceRate)}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="sessions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sessions">All Sessions</TabsTrigger>
          <TabsTrigger value="journey">Journey Map</TabsTrigger>
          <TabsTrigger value="heatmap">Heatmaps</TabsTrigger>
          <TabsTrigger value="replay">Session Replay</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Visitor Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center p-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : sessions.length === 0 ? (
                <p className="text-center text-muted-foreground p-8">No visitor data yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Visitor</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Pages</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((session) => (
                      <TableRow key={session.id}>
                        <TableCell className="font-mono text-xs">
                          {session.visitor_id.substring(0, 8)}...
                        </TableCell>
                        <TableCell>
                          {session.is_returning ? (
                            <Badge variant="secondary">Returning</Badge>
                          ) : (
                            <Badge variant="outline">New</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(session.started_at)}</TableCell>
                        <TableCell>{formatDuration(session.total_duration_ms)}</TableCell>
                        <TableCell>{session.pages_viewed}</TableCell>
                        <TableCell className="capitalize">
                          {session.device} / {session.browser}
                        </TableCell>
                        <TableCell>
                          {session.city && session.country
                            ? `${session.city}, ${session.country}`
                            : session.country || '—'}
                        </TableCell>
                        <TableCell>
                          {session.bounce && <Badge variant="destructive">Bounced</Badge>}
                          {!session.ended_at && <Badge className="bg-green-500">Active</Badge>}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedSession(session.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Journey
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="journey" className="space-y-4">
          {!selectedSession ? (
            <Card>
              <CardContent className="p-8">
                <p className="text-center text-muted-foreground">
                  Select a session from the "All Sessions" tab to view the visitor journey
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Visitor Journey Map</CardTitle>
                <p className="text-sm text-muted-foreground">Session: {selectedSession}</p>
              </CardHeader>
              <CardContent>
                {journey.length === 0 ? (
                  <p className="text-center text-muted-foreground p-8">No journey data available</p>
                ) : (
                  <div className="space-y-4">
                    {journey.map((step, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="flex flex-col items-center">
                          <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-semibold">
                            {index + 1}
                          </div>
                          {index < journey.length - 1 && (
                            <div className="w-0.5 h-16 bg-border mt-2" />
                          )}
                        </div>
                        <Card className="flex-1">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="space-y-1">
                                <h4 className="font-semibold">
                                  {step.page_title || step.page_path}
                                </h4>
                                <p className="text-sm text-muted-foreground">{step.page_path}</p>
                                <div className="flex gap-4 text-sm text-muted-foreground">
                                  <span>⏱️ {formatDuration(step.duration_ms)}</span>
                                  <span>🎯 {step.events_count} events</span>
                                  <span>🕐 {formatDate(step.viewed_at)}</span>
                                </div>
                              </div>
                              {index < journey.length - 1 && (
                                <ArrowRight className="h-5 w-5 text-muted-foreground mt-1" />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="heatmap" className="space-y-4">
          <HeatmapVisualization />
        </TabsContent>

        <TabsContent value="replay" className="space-y-4">
          {selectedSession ? (
            replayEvents.length > 0 ? (
              <SessionReplayPlayer sessionId={selectedSession} events={replayEvents} />
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-center text-muted-foreground">
                    No replay data available for this session
                  </p>
                </CardContent>
              </Card>
            )
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">
                  Select a session from the "All Sessions" tab to watch its replay
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
